/**
 * Thumbnail rendering via pdf.js.
 *
 * pdf.js parses in its own bundled worker; here we drive it to rasterise each
 * page to a canvas for preview only. The output PDF is built separately by
 * pdf-lib from the original bytes, so thumbnail fidelity never affects the file
 * you export — a page that renders imperfectly is still copied losslessly.
 */

import * as pdfjsLib from 'pdfjs-dist';
// Vite-friendly worker wiring: hashed URL to the ESM worker, served same-origin.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export interface PdfHandle {
  numPages: number;
  renderPage(pageIndex: number, targetCssWidth: number): Promise<HTMLCanvasElement>;
  destroy(): void;
}

/**
 * Open a PDF for preview rendering. Takes a *copy* of the bytes because pdf.js
 * detaches the buffer it's handed — the store keeps the pristine original for
 * lossless assembly.
 */
export async function openPdf(bytes: ArrayBuffer): Promise<PdfHandle> {
  const copy = bytes.slice(0);
  const task = pdfjsLib.getDocument({
    data: new Uint8Array(copy),
    isEvalSupported: false,
    // No external cMap/font fetches — stay fully offline. Some exotic glyphs may
    // not preview perfectly; the exported file is unaffected.
    disableAutoFetch: true,
    disableStream: true,
  });
  const pdf = await task.promise;

  const dpr = Math.min(2, Math.max(1, Math.floor(globalThis.devicePixelRatio || 1)));

  return {
    numPages: pdf.numPages,
    async renderPage(pageIndex: number, targetCssWidth: number): Promise<HTMLCanvasElement> {
      const page = await pdf.getPage(pageIndex + 1);
      const base = page.getViewport({ scale: 1 });
      const scale = (targetCssWidth * dpr) / base.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      canvas.style.width = '100%';
      canvas.style.height = 'auto';
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error('Could not get a 2D canvas context for thumbnails.');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // `intent: 'print'` makes pdf.js schedule paint chunks via microtasks
      // instead of requestAnimationFrame — which never fires in a backgrounded
      // (or headless) tab and would otherwise hang thumbnail rendering.
      await page.render({ canvasContext: ctx, viewport, intent: 'print' }).promise;
      page.cleanup();
      return canvas;
    },
    destroy() {
      void pdf.destroy();
    },
  };
}
